"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Sparkles } from "lucide-react";
import { useTheme } from "next-themes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { GoogleOAuthButton } from "@/components/auth/GoogleOAuthButton";
import { AuthDivider } from "@/components/auth/AuthDivider";
import { AccountConfirmationStep } from "@/components/auth/AccountConfirmationStep";
import {
  clearQuoteResumeAfterAuth,
  QUOTE_RESUME_ID_PARAM,
} from "@/utils/quoteResumePersistence";
import SavePoint from "@/utils/savePoint";
import {
  type AccountCredentialErrors,
  validateAccountCredentials,
} from "@/lib/auth/accountCredentialValidation";

const EMAIL_ERROR_ID = "quote-account-email-error";
const PASSWORD_HINT_ID = "quote-account-password-hint";
const PASSWORD_ERROR_ID = "quote-account-password-error";

export interface CreateQuoteAccountResult {
  ok: boolean;
  error?: string;
  requiresConfirmation?: boolean;
}

interface CreateQuoteAccountModalProps {
  open: boolean;
  imageUrl?: string | null;
  onDismiss: () => void;
  /** Return customer-safe copy when navigation must remain on this dress. */
  onSignIn: () => string | void;
  onGoogleContinue?: () => string | void;
  onCreateAccount: (params: {
    email: string;
    password: string;
  }) => Promise<CreateQuoteAccountResult>;
  /** Reload-safe continuation for an inline account upgrade awaiting email. */
  pendingConfirmationEmail?: string | null;
}

/**
 * Account gate for a free instant quote. This intentionally does not mention
 * credits or payment: the visitor already has a dress, and the only remaining
 * requirement is a recoverable account to own the quote.
 */
export function CreateQuoteAccountModal({
  open,
  imageUrl = null,
  onDismiss,
  onSignIn,
  onGoogleContinue,
  onCreateAccount,
  pendingConfirmationEmail = null,
}: CreateQuoteAccountModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<AccountCredentialErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oauthSubmitting, setOauthSubmitting] = useState(false);
  const [step, setStep] = useState<"account" | "confirm">("account");
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const oauthResetTimerRef = useRef<number | null>(null);
  const { resolvedTheme } = useTheme();

  useEffect(
    () => () => {
      if (oauthResetTimerRef.current !== null) {
        window.clearTimeout(oauthResetTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (open) return;

    // This dialog stays mounted between attempts. Never retain a password or
    // stale account error after the visitor deliberately leaves the gate.
    setEmail("");
    setPassword("");
    setFieldErrors({});
    setFormError(null);
    setSubmitting(false);
    setOauthSubmitting(false);
    if (oauthResetTimerRef.current !== null) {
      window.clearTimeout(oauthResetTimerRef.current);
      oauthResetTimerRef.current = null;
    }
    setStep("account");
  }, [open]);

  const visibleStep = open && pendingConfirmationEmail ? "confirm" : step;
  const visibleEmail = pendingConfirmationEmail || email;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const validation = validateAccountCredentials(email, password);
    setFieldErrors(validation.errors);
    setFormError(null);
    if (!validation.isValid) {
      const firstInvalidField = validation.errors.email
        ? emailRef.current
        : passwordRef.current;
      firstInvalidField?.focus({ preventScroll: true });
      firstInvalidField?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await onCreateAccount({
        email: validation.normalizedEmail,
        password,
      });
      if (!result.ok) {
        setFormError(
          result.error ?? "We couldn't create your account. Try again."
        );
      } else if (result.requiresConfirmation) {
        setPassword("");
        setStep("confirm");
      }
    } catch {
      setFormError("We couldn't create your account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    if (visibleStep === "confirm") {
      const url = new URL(window.location.href);
      const intentId = url.searchParams.get(QUOTE_RESUME_ID_PARAM);
      clearQuoteResumeAfterAuth(intentId);
      if (intentId) SavePoint.clear(intentId);
      url.searchParams.delete("quote_intent");
      url.searchParams.delete(QUOTE_RESUME_ID_PARAM);
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    onDismiss();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen && !submitting && !oauthSubmitting) handleDismiss();
      }}
    >
      <DialogContent
        title={null}
        style={{
          maxHeight:
            "calc(100dvh - env(safe-area-inset-top) - max(env(safe-area-inset-bottom), var(--keyboard-inset, 0px)) - 1rem)",
          top: "calc(50% + (env(safe-area-inset-top) - max(env(safe-area-inset-bottom), var(--keyboard-inset, 0px))) / 2)",
        }}
        className="grid max-h-[calc(100svh-1rem)] w-[calc(100vw-2rem)] grid-rows-[auto_minmax(0,1fr)] !gap-0 overflow-hidden !rounded-2xl !p-0 sm:max-w-lg"
      >
        <DialogHeader className="shrink-0 border-b border-border px-4 pb-3 pt-4 text-left [@media(max-height:620px)]:pb-2 [@media(max-height:620px)]:pt-3 sm:px-6 sm:pb-4 sm:pt-6">
          <DialogTitle className="pr-10 text-xl font-semibold tracking-normal sm:text-2xl">
            {visibleStep === "account"
              ? "Get your free instant quote"
              : "Check your email"}
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 sm:text-base sm:leading-6">
            {visibleStep === "account"
              ? "Create a free account to see your quote. No commitment needed."
              : "Confirm your email to save this dress to your account."}
          </DialogDescription>
        </DialogHeader>

        {visibleStep === "account" ? (
          <div
            data-testid="quote-account-scroll-region"
            className="mx-auto grid min-h-0 w-full max-w-md min-w-0 gap-3 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 [@media(max-height:620px)]:gap-2 [@media(max-height:620px)]:pt-2 sm:gap-4 sm:px-6 sm:pt-4"
          >
            <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3 [@media(max-height:620px)]:hidden">
              <div className="relative aspect-[4/5] w-14 shrink-0 overflow-hidden rounded-lg border border-border bg-muted shadow-sm">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt="Your dress design for this quote"
                    fill
                    sizes="56px"
                    className="object-cover object-top"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center bg-primary-muted text-primary">
                    <Sparkles className="size-5" aria-hidden />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  Your design for this quote
                </p>
                <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
                  Your dress and conversation stay together.
                </p>
              </div>
            </div>

            <div className="min-w-0 space-y-3 [@media(max-height:620px)]:space-y-2 sm:space-y-4">
              <div className="[&>button]:mb-2 [&>div]:mb-2 [@media(max-height:620px)]:[&>button]:mb-1 [@media(max-height:620px)]:[&>div]:mb-1">
                <GoogleOAuthButton
                  onClick={() => {
                    setFormError(null);
                    setOauthSubmitting(true);
                    try {
                      const navigationError = onGoogleContinue?.();
                      if (navigationError) {
                        setFormError(navigationError);
                        setOauthSubmitting(false);
                        return;
                      }
                      // A blocked navigation must not leave the dialog inert.
                      oauthResetTimerRef.current = window.setTimeout(
                        () => setOauthSubmitting(false),
                        10_000
                      );
                    } catch {
                      setFormError(
                        "We couldn't open sign-in. Your dress is still here. Please try again."
                      );
                      setOauthSubmitting(false);
                    }
                  }}
                  disabled={!onGoogleContinue || submitting}
                  loading={oauthSubmitting}
                  isDarkMode={resolvedTheme === "dark"}
                />
                <AuthDivider />
              </div>

              <form
                className="space-y-2.5 [@media(max-height:620px)]:space-y-2 sm:space-y-3"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="space-y-1">
                  <label
                    htmlFor="quote-account-email"
                    className="block text-xs font-medium text-foreground sm:text-sm"
                  >
                    Email
                  </label>
                  <input
                    ref={emailRef}
                    id="quote-account-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={email}
                    onChange={event => {
                      setEmail(event.target.value);
                      setFieldErrors(current => ({
                        ...current,
                        email: undefined,
                      }));
                      setFormError(null);
                    }}
                    disabled={submitting || oauthSubmitting}
                    aria-invalid={Boolean(fieldErrors.email)}
                    aria-describedby={
                      fieldErrors.email ? EMAIL_ERROR_ID : undefined
                    }
                    placeholder="you@example.com"
                    className="h-10 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-11"
                  />
                  {fieldErrors.email ? (
                    <span
                      id={EMAIL_ERROR_ID}
                      role="alert"
                      className="block text-sm text-destructive"
                    >
                      {fieldErrors.email}
                    </span>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="quote-account-password"
                    className="block text-xs font-medium text-foreground sm:text-sm"
                  >
                    Password
                  </label>
                  <input
                    ref={passwordRef}
                    id="quote-account-password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={event => {
                      setPassword(event.target.value);
                      setFieldErrors(current => ({
                        ...current,
                        password: undefined,
                      }));
                      setFormError(null);
                    }}
                    disabled={submitting || oauthSubmitting}
                    aria-invalid={Boolean(fieldErrors.password)}
                    aria-describedby={
                      fieldErrors.password
                        ? `${PASSWORD_HINT_ID} ${PASSWORD_ERROR_ID}`
                        : PASSWORD_HINT_ID
                    }
                    minLength={8}
                    placeholder="Create a password"
                    className="h-10 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-11"
                  />
                  <span
                    id={PASSWORD_HINT_ID}
                    className="block text-[11px] leading-4 text-muted-foreground sm:text-xs sm:leading-5"
                  >
                    <span className="[@media(max-height:620px)]:hidden">
                      At least 8 characters, with uppercase and lowercase
                      letters and a number.
                    </span>
                    <span className="hidden whitespace-nowrap text-[10px] [@media(max-height:620px)]:inline">
                      8+ characters · A–Z, a–z, 0–9
                    </span>
                  </span>
                  {fieldErrors.password ? (
                    <span
                      id={PASSWORD_ERROR_ID}
                      role="alert"
                      className="block text-sm text-destructive"
                    >
                      {fieldErrors.password}
                    </span>
                  ) : null}
                </div>

                {formError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {formError}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  variant="gradient"
                  disabled={submitting || oauthSubmitting}
                  className="h-11 w-full rounded-lg text-sm font-semibold sm:h-12 sm:text-base"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Saving your dress…
                    </>
                  ) : (
                    "Create account and see quote"
                  )}
                </Button>
              </form>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setFormError(null);
                  try {
                    const navigationError = onSignIn();
                    if (navigationError) setFormError(navigationError);
                  } catch {
                    setFormError(
                      "We couldn't open sign-in. Your dress is still here. Please try again."
                    );
                  }
                }}
                disabled={submitting || oauthSubmitting}
                className="h-9 w-full rounded-lg text-sm font-semibold text-muted-foreground shadow-none hover:text-foreground [@media(max-height:620px)]:h-8 sm:h-10"
              >
                Already have an account? Sign in
              </Button>
            </div>
          </div>
        ) : (
          <div
            data-testid="quote-account-scroll-region"
            className="min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
          >
            <AccountConfirmationStep
              email={visibleEmail}
              title="Confirm your email"
              description="We sent a confirmation link to"
              detail={
                <p className="text-sm font-medium text-foreground">
                  Your dress and chat are saved.
                </p>
              }
              footnote="Your quote opens automatically after confirmation."
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
